import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Project } from '../api/entities.js';
import { Company } from '../api/entities.js';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Input } from "../components/ui/input.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { DatePickerWithRange } from "../components/ui/date-picker.jsx";
import { 
  Plus, 
  RefreshCw, 
  Search, 
  Filter,
  LayoutGrid,
  List,
  Calendar,
  Users,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  Download,
  Settings
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import ProjectsKanban from '../components/projects/ProjectsKanban.jsx';
import ProjectsList from '../components/projects/ProjectsList.jsx';
import ProjectStats from '../components/projects/ProjectStats.jsx';

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [managerFilter, setManagerFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadData();
  }, [refreshKey]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Carregar todas as empresas
      let companiesData = [];
      try {
        companiesData = await Company.list();
      } catch (error) {
        console.error('Error loading companies:', error);
      }

      // Carregar projetos
      const projectsData = await Project.list();
      
      // Mapear projetos com informações das empresas
      const mappedProjects = projectsData.map(project => {
        let companyInfo = { 
          corporate_name: 'Cliente não encontrado',
          name: 'Cliente não encontrado'
        };
        
        if (project.client_id && companiesData.length > 0) {
          const foundCompany = companiesData.find(c => c.id === project.client_id);
          if (foundCompany) {
            companyInfo = foundCompany;
          }
        }
        
        return {
          ...project,
          company: companyInfo
        };
      });
      
      setProjects(mappedProjects);
      setClients(companiesData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setIsLoading(false);
  };

  // Calculate statistics
  const calculateStats = () => {
    const total = projects.length;
    const planning = projects.filter(p => p.status === 'planning').length;
    const inProgress = projects.filter(p => p.status === 'in_progress').length;
    const paused = projects.filter(p => p.status === 'paused').length;
    const completed = projects.filter(p => p.status === 'completed').length;
    const overdue = projects.filter(p => {
      if (!p.end_date) return false;
      const endDate = new Date(p.end_date);
      const today = new Date();
      return endDate < today && p.status !== 'completed';
    }).length;

    return {
      total,
      planning,
      inProgress,
      paused,
      completed,
      overdue,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  // Filter projects based on search and filters
  const getFilteredProjects = () => {
    return projects.filter(project => {
      const matchesSearch = searchQuery === '' || 
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (project.company?.corporate_name || project.company?.name)?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      const matchesClient = clientFilter === 'all' || project.client_id === clientFilter;
      const matchesManager = managerFilter === 'all' || project.manager === managerFilter;

      const matchesDateRange = !dateRange.from || !dateRange.to || (
        project.start_date && 
        new Date(project.start_date) >= dateRange.from && 
        new Date(project.start_date) <= dateRange.to
      );

      return matchesSearch && matchesStatus && matchesClient && matchesManager && matchesDateRange;
    });
  };

  // Get unique managers for filter
  const getUniqueManagers = () => {
    const managers = projects
      .map(p => p.manager)
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
    return managers;
  };

  const viewProject = (projectId) => {
    navigate(createPageUrl(`ViewProject?id=${projectId}`));
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setClientFilter('all');
    setManagerFilter('all');
    setDateRange({ from: null, to: null });
  };

  const stats = calculateStats();
  const filteredProjects = getFilteredProjects();

  return (
    <div className="space-y-6">
      {/* Modern Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Projetos</h1>
            <p className="text-blue-100 text-lg">
              Gerencie todos os projetos da empresa em um só lugar
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => navigate(createPageUrl('Reports'))}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Relatórios
            </Button>
            <Button
              variant="secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button
              onClick={() => navigate(createPageUrl('NewProject'))}
              className="bg-white text-blue-600 hover:bg-white/90 font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Projeto
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Statistics Cards */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4"
      >
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total</p>
                <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
              </div>
              <LayoutGrid className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Planejamento</p>
                <p className="text-2xl font-bold text-purple-900">{stats.planning}</p>
              </div>
              <Clock className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Em Andamento</p>
                <p className="text-2xl font-bold text-blue-900">{stats.inProgress}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Pausados</p>
                <p className="text-2xl font-bold text-orange-900">{stats.paused}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Concluídos</p>
                <p className="text-2xl font-bold text-green-900">{stats.completed}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Atrasados</p>
                <p className="text-2xl font-bold text-red-900">{stats.overdue}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-600 font-medium">Taxa Conclusão</p>
                <p className="text-2xl font-bold text-indigo-900">{stats.completionRate}%</p>
              </div>
              <BarChart3 className="w-8 h-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Search and Filters */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border p-6 space-y-4"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar projetos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="w-4 h-4" />
              Filtros
              {(statusFilter !== 'all' || clientFilter !== 'all' || managerFilter !== 'all') && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs">
                  !
                </Badge>
              )}
            </Button>
            {(searchQuery || statusFilter !== 'all' || clientFilter !== 'all' || managerFilter !== 'all') && (
              <Button variant="ghost" onClick={clearFilters} size="sm">
                Limpar
              </Button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 border-t pt-4"
            >
              <div>
                <label className="text-sm font-medium mb-2 block">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="planning">Planejamento</SelectItem>
                    <SelectItem value="in_progress">Em Andamento</SelectItem>
                    <SelectItem value="paused">Pausado</SelectItem>
                    <SelectItem value="completed">Concluído</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Cliente</label>
                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os clientes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os clientes</SelectItem>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name || client.corporate_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Gestor</label>
                <Select value={managerFilter} onValueChange={setManagerFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os gestores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os gestores</SelectItem>
                    {getUniqueManagers().map(manager => (
                      <SelectItem key={manager} value={manager}>
                        {manager}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Período</label>
                <DatePickerWithRange
                  date={dateRange}
                  onDateChange={setDateRange}
                  className="w-full"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Results Count and View Toggle */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="text-sm text-muted-foreground">
          Exibindo {filteredProjects.length} de {projects.length} projetos
          {filteredProjects.length !== projects.length && (
            <Badge variant="secondary" className="ml-2">
              Filtrado
            </Badge>
          )}
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="kanban" className="gap-2">
              <LayoutGrid className="w-4 h-4" />
              Kanban
            </TabsTrigger>
            <TabsTrigger value="list" className="gap-2">
              <List className="w-4 h-4" />
              Lista
            </TabsTrigger>
            <TabsTrigger value="stats" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Estatísticas
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </motion.div>

      {/* Project Views */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Tabs value={view} onValueChange={setView}>
          <TabsContent value="kanban" className="mt-0">
            <ProjectsKanban 
              projects={filteredProjects} 
              clients={clients} 
              isLoading={isLoading}
              onViewProject={viewProject}
              navigate={navigate}
            />
          </TabsContent>
          <TabsContent value="list" className="mt-0">
            <ProjectsList 
              projects={filteredProjects} 
              companies={clients} 
              isLoading={isLoading}
              onViewProject={viewProject}
              navigate={navigate}
            />
          </TabsContent>
          <TabsContent value="stats" className="mt-0">
            <ProjectStats 
              projects={filteredProjects}
            />
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Help Text */}
      {!isLoading && filteredProjects.length > 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center text-sm text-muted-foreground bg-muted/30 rounded-lg p-4"
        >
          💡 <strong>Dica:</strong> No modo Kanban, arraste e solte os cards para alterar o status dos projetos automaticamente.
        </motion.div>
      )}

      {/* Empty State */}
      {!isLoading && filteredProjects.length === 0 && projects.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-12"
        >
          <Filter className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhum projeto encontrado</h3>
          <p className="text-muted-foreground mb-4">
            Tente ajustar os filtros ou busca para encontrar projetos.
          </p>
          <Button onClick={clearFilters} variant="outline">
            Limpar filtros
          </Button>
        </motion.div>
      )}

      {/* Completely Empty State */}
      {!isLoading && projects.length === 0 && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-20"
        >
          <LayoutGrid className="w-24 h-24 text-muted-foreground mx-auto mb-6" />
          <h3 className="text-2xl font-semibold mb-4">Comece criando seu primeiro projeto</h3>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Organize e gerencie todos os projetos da empresa em um só lugar com nossa ferramenta completa.
          </p>
          <Button
            onClick={() => navigate(createPageUrl('NewProject'))}
            size="lg"
            className="gap-2"
          >
            <Plus className="w-5 h-5" />
            Criar Primeiro Projeto
          </Button>
        </motion.div>
      )}
    </div>
  );
}
